import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { InfoHint } from '@/components/ui/info-hint';
import { SaveButton } from '@/components/ui/save-button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { PermissionDenied } from '@/components/ui/permission-denied';
import type { ApiError } from '@/core/api';
import type { OrganizationDto } from '@/features/auth/types';
import { useSavedFlash } from '@/hooks/useSavedFlash';
import { COUNTRIES, countryName } from '@/lib/countries';
import { useSession } from '@/stores/session';
import { CompanyBankAccountsCard } from '../components/CompanyBankAccountsCard';
import { MONTHS } from '../constants';
import { useOrganization, useUpdateOrganization, useUploadOrgLogo } from '../queries';

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255),
  fiscal_year_start_month: z.string(),
  legal_name: z.string().max(255),
  legal_name_local: z.string().max(255),
  address: z.string(),
  address_local: z.string(),
  contact_email: z.string().refine((v) => v === '' || /.+@.+\..+/.test(v), 'Enter a valid email'),
  registration_number: z.string().max(100),
  country: z.string().max(100),
  staff_invoice_prefix: z.string().max(16),
  client_invoice_prefix: z.string().max(16),
  default_commission_basis: z.enum(['none', 'first_client_invoice', 'all_paid_invoices']),
  primary_color: z
    .string()
    .refine((v) => v === '' || HEX.test(v), 'Enter a hex color like #2563eb'),
  manual_time_logging_enabled: z.boolean(),
  timer_note_mode: z.enum(['optional', 'required']),
});

type FormValues = z.infer<typeof schema>;
const FIELD_KEYS = [
  'name', 'fiscal_year_start_month', 'legal_name', 'legal_name_local', 'address', 'address_local', 'contact_email',
  'registration_number', 'country', 'staff_invoice_prefix', 'client_invoice_prefix',
  'default_commission_basis', 'primary_color', 'timer_note_mode',
] as const;

const COUNTRY_NONE = '__none__';

/** Mirrors finance CommissionBasis (ADR-046). */
const BASIS_LABELS = {
  none: 'No commission',
  first_client_invoice: 'First client invoice (new clients only)',
  all_paid_invoices: 'All paid invoices',
} as const;

/** Mirrors projects TimerNoteMode (BE-ADR-109). */
const TIMER_NOTE_LABELS = {
  optional: 'Optional — can be skipped',
  required: 'Required',
} as const;

export function OrganizationSettingsPage() {
  const organizations = useSession((s) => s.organizations);
  const activeOrgSlug = useSession((s) => s.activeOrgSlug);
  const activeOrg = organizations.find((o) => o.slug === activeOrgSlug);
  const orgId = activeOrg?.id ?? -1;

  const orgQuery = useOrganization(orgId);
  const uploadLogo = useUploadOrgLogo(orgId);
  const org = orgQuery.data;

  return (
    <div className="max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Organization</h1>
        <p className="text-muted-foreground text-sm">
          Workspace settings for the active organization.
        </p>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-3">
      {org ? (
        <Card>
          <CardHeader>
            <CardTitle>Logo</CardTitle>
            <CardDescription>Shown in the header for everyone in this workspace.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-4">
            {org.logo ? (
              <img
                src={org.logo}
                alt={`${org.name} logo`}
                className="size-16 rounded-md border object-cover"
              />
            ) : (
              <div className="bg-muted text-muted-foreground grid size-16 place-items-center rounded-md border text-xs">
                No logo
              </div>
            )}
            <div className="space-y-2">
              <Input
                type="file"
                accept="image/*"
                aria-label="Upload logo"
                disabled={uploadLogo.isPending}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadLogo.mutate(file);
                  e.target.value = ''; // allow re-upload of the same file
                }}
              />
              <p className="text-muted-foreground text-xs">
                PNG / JPG / SVG. Square works best.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {orgQuery.isPending ? (
        <Skeleton className="h-72 w-full" />
      ) : orgQuery.isError ? (
        (orgQuery.error as unknown as ApiError)?.status === 403 ? (
          <PermissionDenied message={(orgQuery.error as unknown as ApiError)?.message} />
        ) : (
          <p className="text-destructive text-sm">
            {(orgQuery.error as unknown as ApiError)?.message ?? 'Failed to load organization.'}
          </p>
        )
      ) : (
        org ? (
          <OrgDetailsCard org={org} orgId={orgId} />
        ) : null
      
      )}
        </div>

        <div className="space-y-6 lg:col-span-2">
          {org ? <CompanyBankAccountsCard /> : null}
        </div>
      </div>
    </div>
  );
}


/**
 * The details form mounts only once the org is loaded, so every select's value
 * is set at mount and never changes programmatically afterwards. That matters:
 * a Radix Select inside a <form> mirrors its value into a hidden native
 * <select> whose <option>s exist only after the dropdown has been opened —
 * change the value while it's closed and the mirror coerces it to '' and
 * echoes that back through onValueChange, silently wiping the field.
 * (DealFormPage gates on the loaded deal for the same reason.)
 */
function OrgDetailsCard({ org, orgId }: { org: OrganizationDto; orgId: number }) {
  const update = useUpdateOrganization(orgId);
  const { saved, flash } = useSavedFlash();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: org.name,
      fiscal_year_start_month: String(org.fiscal_year_start_month),
      legal_name: org.legal_name ?? '',
      legal_name_local: org.legal_name_local ?? '',
      address: org.address ?? '',
      address_local: org.address_local ?? '',
      contact_email: org.contact_email ?? '',
      registration_number: org.registration_number ?? '',
      // Store the full name; map any legacy alpha-2 code to its name on load.
      country: countryName(org.country),
      staff_invoice_prefix: org.staff_invoice_prefix ?? '',
      client_invoice_prefix: org.client_invoice_prefix ?? '',
      default_commission_basis: org.default_commission_basis ?? 'first_client_invoice',
      primary_color: org.primary_color ?? '',
      manual_time_logging_enabled: org.manual_time_logging_enabled ?? true,
      timer_note_mode: org.timer_note_mode ?? 'optional',
    },
  });

  async function handleSubmit(values: FormValues) {
    try {
      await update.mutateAsync({
        name: values.name,
        fiscal_year_start_month: Number(values.fiscal_year_start_month),
        legal_name: values.legal_name,
        legal_name_local: values.legal_name_local,
        address: values.address,
        address_local: values.address_local,
        contact_email: values.contact_email,
        registration_number: values.registration_number,
        country: values.country,
        staff_invoice_prefix: values.staff_invoice_prefix,
        client_invoice_prefix: values.client_invoice_prefix,
        default_commission_basis: values.default_commission_basis,
        primary_color: values.primary_color,
        manual_time_logging_enabled: values.manual_time_logging_enabled,
        timer_note_mode: values.timer_note_mode,
      });
      // Saved values are the new baseline — clears isDirty without changing any
      // field value, so the Radix mirror sees no change.
      form.reset(values);
      flash();
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Details</CardTitle>
        <CardDescription>
          Slug and base currency are fixed after provisioning.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Slug</Label>
                <Input value={org?.slug ?? ''} disabled readOnly />
                <p className="text-muted-foreground text-[0.8rem]">
                  The tenant key — cannot be changed.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Base currency</Label>
                <Input value={org?.base_currency ?? ''} disabled readOnly />
                <p className="text-muted-foreground text-[0.8rem]">
                  Fixed at provisioning (USD base).
                </p>
              </div>
            </div>

            <FormField
              control={form.control}
              name="fiscal_year_start_month"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fiscal year starts</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="sm:w-1/2">
                        {/* Render the label from the value: Radix resolves item
                            text only from mounted items, so labels stay
                            deterministic even before the dropdown ever opens. */}
                        <SelectValue>
                          {MONTHS.find((m) => String(m.value) === field.value)?.label}
                        </SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MONTHS.map((m) => (
                        <SelectItem key={m.value} value={String(m.value)}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-1">
              <h3 className="text-sm font-semibold">Invoice issuer details</h3>
              <p className="text-muted-foreground text-[0.8rem]">
                Printed on staff-invoice PDFs (the “bill from” block).
              </p>
            </div>

            <FormField
              control={form.control}
              name="legal_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Legal name
                    <InfoHint text="Registered entity name. Falls back to the org name." />
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="FLEXIPY OÜ" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="legal_name_local"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Legal name (local language)
                    <InfoHint text="Second column of the bilingual contractor invoice — e.g. the entity name transliterated for the supplier's bank. Blank = English-only invoice." />
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="ФЛЕКСІПАЙ ОЮ" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-6 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Textarea rows={3} placeholder="Sepapaja 6, Tallinn&#10;15551, Estonia" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address_local"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address (local language)</FormLabel>
                    <FormControl>
                      <Textarea rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="contact_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="contact@flexipy.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid items-start gap-6 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="registration_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      Registration / Tax ID
                      <InfoHint text="Printed on client invoices." />
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="EE123456789" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <Select
                      value={field.value || COUNTRY_NONE}
                      onValueChange={(v) => field.onChange(v === COUNTRY_NONE ? '' : v)}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a country">
                            {field.value || '—'}
                          </SelectValue>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={COUNTRY_NONE}>—</SelectItem>
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c.code} value={c.name}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="staff_invoice_prefix"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      Staff invoice prefix
                      <InfoHint text="Leads staff-invoice numbers: {prefix}-{employee code}-{year}-001." />
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="INV" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="client_invoice_prefix"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      Client invoice prefix
                      <InfoHint text="Leads client-invoice numbers — one gap-less series: {prefix}-{year}-0001." />
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="INV" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="default_commission_basis"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1.5">
                    Default commission basis
                    <InfoHint text="Prefilled onto new deals: pay commission on the client's first paid invoice (new clients only), on every paid invoice, or not at all. Overridable per deal." />
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue>{BASIS_LABELS[field.value]}</SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(BASIS_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="primary_color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Primary color</FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-2">
                      <Input
                        type="color"
                        // <input type=color> only accepts #rrggbb — fall back when empty.
                        value={field.value || '#2563eb'}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="h-9 w-12 cursor-pointer p-1"
                      />
                      <Input
                        value={field.value}
                        placeholder="#2563eb"
                        onChange={(e) => field.onChange(e.target.value)}
                        className="font-mono"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="manual_time_logging_enabled"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-md border px-3 py-2">
                  <div className="space-y-0.5 pr-4">
                    <FormLabel className="m-0 flex items-center gap-1.5">
                      Manual time logging
                      <InfoHint text="Let people log time by hand on the Worklogs page. When off, time is captured only by the work-item timer, and people can edit only the note on their entries — not change or delete the time. Founder, Super Admin and Accountant are exempt." />
                    </FormLabel>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="timer_note_mode"
              render={({ field }) => (
                <FormItem className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2">
                  <div className="space-y-0.5 pr-4">
                    <FormLabel className="m-0 flex items-center gap-1.5">
                      Timer notes
                      <InfoHint text="Stopping the work-item timer asks what was done — a short memo (up to 255 characters) shown on worklogs and timesheets. Required also applies to logging and editing time by hand, for everyone." />
                    </FormLabel>
                  </div>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-56" aria-label="Timer notes">
                        <SelectValue>{TIMER_NOTE_LABELS[field.value]}</SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {(Object.keys(TIMER_NOTE_LABELS) as (keyof typeof TIMER_NOTE_LABELS)[]).map(
                        (mode) => (
                          <SelectItem key={mode} value={mode}>
                            {TIMER_NOTE_LABELS[mode]}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage className="basis-full" />
                </FormItem>
              )}
            />

            {form.formState.errors.root ? (
              <p className="text-destructive text-sm">
                {form.formState.errors.root.message}
              </p>
            ) : null}

            <SaveButton
              type="submit"
              pending={form.formState.isSubmitting}
              saved={saved && !form.formState.isDirty}
              disabled={!form.formState.isDirty}
            >
              Save changes
            </SaveButton>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
